import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  PortalLoadingCard,
  PortalMetricCard,
  PortalStateCard,
  getAccessStatusMeta,
  usePortalTableStyles
} from './portalShared'

const useStyles = makeStyles((theme) => ({
  crewCard: {
    borderRadius: 'var(--df-radius-md)',
    border: '1px solid var(--df-line)',
    background: 'var(--df-surface)',
    height: '100%'
  },
  crewName: {
    fontWeight: 700,
    color: 'var(--df-ink)'
  },
  crewMeta: {
    color: 'var(--df-ink-3)',
    marginTop: theme.spacing(0.75)
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5)
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  memberRow: {
    borderRadius: 'var(--df-radius-sm)',
    border: '1px solid var(--df-line)',
    padding: theme.spacing(1.25),
    '& + &': {
      marginTop: theme.spacing(1)
    }
  },
  memberName: {
    fontWeight: 700,
    color: 'var(--df-ink)'
  },
  memberMeta: {
    color: 'var(--df-ink-3)'
  },
  dialogHint: {
    color: 'var(--df-ink-3)',
    marginBottom: theme.spacing(2)
  },
  emptyCell: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  }
}))

const defaultFormState = {
  name: '',
  territoryLabel: '',
  notes: '',
  memberIds: []
}

const SubcontractorCrews = ({ user, notification }) => {
  const classes = useStyles()
  const shared = usePortalTableStyles()
  const isAdmin = user?.subcontractorProfile?.role === 'admin'
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [availableMembers, setAvailableMembers] = useState([])
  const [summary, setSummary] = useState({})
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formState, setFormState] = useState(defaultFormState)
  const [editingCrew, setEditingCrew] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [busyCrewId, setBusyCrewId] = useState('')

  const loadCrews = useCallback(async () => {
    if (!isAdmin) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await axios.get('/api/subcontractor/crews', {
        params: {
          includeInactive: true
        }
      })
      setItems(response.data?.items || [])
      setAvailableMembers(response.data?.availableMembers || [])
      setSummary(response.data?.summary || {})
    } catch (requestError) {
      console.error('Failed to load subcontractor crews', requestError)
      setError(requestError?.response?.data?.message || 'Unable to load subcontractor crews right now.')
    } finally {
      setLoading(false)
    }
  }, [isAdmin])

  useEffect(() => {
    loadCrews()
  }, [loadCrews])

  const openCreateDialog = () => {
    setEditingCrew(null)
    setFormState(defaultFormState)
    setDialogOpen(true)
  }

  const openEditDialog = (crew) => {
    setEditingCrew(crew)
    setFormState({
      name: crew?.name || '',
      territoryLabel: crew?.territoryLabel || '',
      notes: crew?.notes || '',
      memberIds: Array.isArray(crew?.members) ? crew.members.map(member => member._id) : []
    })
    setDialogOpen(true)
  }

  const closeDialog = () => {
    if (submitting) return
    setDialogOpen(false)
    setEditingCrew(null)
    setFormState(defaultFormState)
  }

  const toggleMember = (memberId) => {
    setFormState((current) => ({
      ...current,
      memberIds: current.memberIds.includes(memberId)
        ? current.memberIds.filter(id => id !== memberId)
        : [...current.memberIds, memberId]
    }))
  }

  const handleSubmit = async () => {
    if (!formState.name.trim()) {
      notification?.({
        type: 'danger',
        title: 'Crew Name Required',
        message: 'Give the crew a name before saving it.'
      })
      return
    }

    setSubmitting(true)

    try {
      const payload = {
        name: formState.name,
        territoryLabel: formState.territoryLabel,
        notes: formState.notes,
        memberIds: formState.memberIds
      }

      if (editingCrew?._id) {
        await axios.put(`/api/subcontractor/crews/${editingCrew._id}`, payload)
      } else {
        await axios.post('/api/subcontractor/crews', payload)
      }

      notification?.({
        type: 'success',
        title: editingCrew?._id ? 'Crew Updated' : 'Crew Created',
        message: editingCrew?._id
          ? `${formState.name.trim()} is ready to use.`
          : `${formState.name.trim()} has been created.`
      })
      closeDialog()
      await loadCrews()
    } catch (requestError) {
      console.error('Failed to save subcontractor crew', requestError)
      notification?.({
        type: 'danger',
        title: 'Crew Save Failed',
        message: requestError?.response?.data?.message || 'Unable to save that crew right now.'
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleArchive = async (crew) => {
    const confirmed = window.confirm(`Archive ${crew.name}? Users currently assigned to it will become unassigned.`)
    if (!confirmed) return

    setBusyCrewId(crew._id)

    try {
      await axios.post(`/api/subcontractor/crews/${crew._id}/archive`)
      notification?.({
        type: 'success',
        title: 'Crew Archived',
        message: `${crew.name} is now inactive.`
      })
      await loadCrews()
    } catch (requestError) {
      console.error('Failed to archive subcontractor crew', requestError)
      notification?.({
        type: 'danger',
        title: 'Archive Failed',
        message: requestError?.response?.data?.message || 'Unable to archive that crew right now.'
      })
    } finally {
      setBusyCrewId('')
    }
  }

  const handleRestore = async (crew) => {
    setBusyCrewId(crew._id)

    try {
      await axios.post(`/api/subcontractor/crews/${crew._id}/restore`)
      notification?.({
        type: 'success',
        title: 'Crew Restored',
        message: `${crew.name} is active again.`
      })
      await loadCrews()
    } catch (requestError) {
      console.error('Failed to restore subcontractor crew', requestError)
      notification?.({
        type: 'danger',
        title: 'Restore Failed',
        message: requestError?.response?.data?.message || 'Unable to restore that crew right now.'
      })
    } finally {
      setBusyCrewId('')
    }
  }

  const summaryCards = useMemo(() => ([
    {
      eyebrow: 'Active Crews',
      value: summary.active || 0,
      caption: 'Crews available for scheduling and territory assignment.'
    },
    {
      eyebrow: 'Inactive Crews',
      value: summary.inactive || 0,
      caption: 'Crews archived and no longer in use.'
    },
    {
      eyebrow: 'Assigned Users',
      value: summary.assignedMembers || 0,
      caption: 'Team members currently assigned to a crew.'
    },
    {
      eyebrow: 'Unassigned Users',
      value: summary.unassignedMembers || 0,
      caption: 'Team members not yet assigned to a crew.'
    }
  ]), [summary])

  const sortedMembers = useMemo(() => {
    return [...availableMembers].sort((a, b) => {
      const aSelected = formState.memberIds.includes(a._id) ? 0 : 1
      const bSelected = formState.memberIds.includes(b._id) ? 0 : 1
      if (aSelected !== bSelected) return aSelected - bSelected
      return a.displayName.localeCompare(b.displayName)
    })
  }, [availableMembers, formState.memberIds])

  if (!isAdmin) {
    return (
      <PortalStateCard
        title="Crew management is admin-only"
        message="Only admins can create crews or reassign team members. Applicators can view their own crew."
        tone="muted"
        minHeight={240}
      />
    )
  }

  if (loading && !items.length) {
    return <PortalLoadingCard title="Loading crews" message="Loading your crews..." minHeight={280} />
  }

  return (
    <div>
      <Grid container spacing={3}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.eyebrow}>
            <PortalMetricCard
              eyebrow={card.eyebrow}
              value={card.value}
              caption={card.caption}
            />
          </Grid>
        ))}
      </Grid>

      <Box mt={3}>
        <Card className={shared.controlsCard}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gridGap={12}>
              <div>
                <Typography variant="h6" className={shared.cardTitle}>
                  Your Crews
                </Typography>
                <Typography variant="body2" className={shared.helperText}>
                  Create and manage your crews here. Assign team members to crews so territories and routes stay organized.
                </Typography>
              </div>
              <Box display="flex" gridGap={8} flexWrap="wrap">
                <Chip label={`Total ${summary.total || items.length || 0}`} className="df-chip-info" />
                <Chip label={`${availableMembers.length} assignable users`} className="df-chip-ok" />
                <Button color="primary" variant="contained" onClick={openCreateDialog}>
                  Create Crew
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {error && !items.length ? (
        <PortalStateCard
          title="Crews unavailable"
          message={error}
          actionLabel="Retry"
          onAction={loadCrews}
          tone="danger"
          minHeight={240}
        />
      ) : (
        <div>
            {items.length ? (
              <Grid container spacing={3}>
                {items.map((crew) => (
                  <Grid item xs={12} md={6} key={crew._id}>
                    <Card className={classes.crewCard} elevation={0}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gridGap={12}>
                          <div>
                            <Typography variant="h6" className={classes.crewName}>
                              {crew.name}
                            </Typography>
                            <Typography variant="body2" className={classes.crewMeta}>
                              {crew.territoryLabel || 'No territory label yet'}
                            </Typography>
                          </div>
                          <Chip
                            label={crew.isActive ? 'Active' : 'Archived'}
                            className={crew.isActive ? 'df-chip-ok' : ''}
                          />
                        </Box>

                        {crew.notes ? (
                          <Typography variant="body2" className={classes.crewMeta}>
                            {crew.notes}
                          </Typography>
                        ) : null}

                        <Box className={classes.chipRow}>
                          <Chip size="small" label={`${crew.memberCount || 0} member${crew.memberCount === 1 ? '' : 's'}`} className="df-chip-info" />
                          {(crew.crewOptions || []).includes('sprayContractor') ? <Chip size="small" label="Spray contractor" className="df-chip-info" /> : null}
                          {(crew.crewOptions || []).includes('subContractedHerbicide') ? <Chip size="small" label="Herbicide" className="df-chip-ok" /> : null}
                        </Box>

                        <Divider style={{ marginTop: 16, marginBottom: 16 }} />

                        {crew.members?.length ? (
                          <Box display="flex" flexDirection="column" gridGap={8}>
                            {crew.members.map((member) => {
                              const accessStatus = getAccessStatusMeta(member.accessStatus)

                              return (
                                <div key={member._id} className={classes.memberRow}>
                                  <Typography variant="body2" className={classes.memberName}>
                                    {member.displayName}
                                  </Typography>
                                  <Typography variant="body2" className={classes.memberMeta}>
                                    {member.role === 'admin' ? 'Admin' : 'Applicator'}
                                  </Typography>
                                  <Box className={classes.chipRow}>
                                    <Chip size="small" label={accessStatus.label} className={accessStatus.className} />
                                  </Box>
                                </div>
                              )
                            })}
                          </Box>
                        ) : (
                          <Typography variant="body2" className={shared.helperText}>
                            No team members assigned to this crew yet.
                          </Typography>
                        )}

                        <div className={classes.actionRow}>
                          <Button variant="outlined" color="primary" onClick={() => openEditDialog(crew)}>
                            Edit Crew
                          </Button>
                          {crew.isActive ? (
                            <Button
                              className="df-danger"
                              disabled={busyCrewId === crew._id}
                              onClick={() => handleArchive(crew)}
                            >
                              {busyCrewId === crew._id ? 'Archiving...' : 'Archive'}
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              color="primary"
                              disabled={busyCrewId === crew._id}
                              onClick={() => handleRestore(crew)}
                            >
                              {busyCrewId === crew._id ? 'Restoring...' : 'Restore'}
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <div className={classes.emptyCell}>
                <PortalStateCard
                  title="No crews created yet"
                  message="No crews yet. Create your first crew to start organizing your team."
                  actionLabel="Create Crew"
                  onAction={openCreateDialog}
                  minHeight={200}
                />
              </div>
            )}
        </div>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingCrew?._id ? `Edit Crew - ${editingCrew.name}` : 'Create Crew'}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" className={classes.dialogHint}>
            Check the team members you want on this crew. If someone is already on another crew, saving will move them here.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Crew Name"
                variant="outlined"
                value={formState.name}
                onChange={(event) => setFormState((current) => ({ ...current, name: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Territory Label"
                variant="outlined"
                value={formState.territoryLabel}
                onChange={(event) => setFormState((current) => ({ ...current, territoryLabel: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes"
                variant="outlined"
                value={formState.notes}
                onChange={(event) => setFormState((current) => ({ ...current, notes: event.target.value }))}
              />
            </Grid>
          </Grid>

          <Box mt={3}>
            <Typography variant="subtitle2" className={shared.cardTitle}>
              Assign Users
            </Typography>
            <Typography variant="body2" className={shared.helperText}>
              These are your team members. Unchecked members stay on their current crew or remain unassigned.
            </Typography>
          </Box>

          <Box mt={2}>
            {sortedMembers.length ? sortedMembers.map((member) => {
              const accessStatus = getAccessStatusMeta(member.accessStatus)
              const isSelected = formState.memberIds.includes(member._id)
              const isMovingFromAnotherCrew = member.currentCrewId && member.currentCrewId !== editingCrew?._id

              return (
                <div key={member._id} className={classes.memberRow}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gridGap={12}>
                    <div>
                      <Typography variant="body2" className={classes.memberName}>
                        {member.displayName}
                      </Typography>
                      <Typography variant="body2" className={classes.memberMeta}>
                        {member.role === 'admin' ? 'Admin' : 'Applicator'} · {member.currentCrewName || 'Unassigned'}
                      </Typography>
                    </div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={isSelected}
                          onChange={() => toggleMember(member._id)}
                        />
                      }
                      label={isSelected ? 'Assigned' : 'Assign'}
                    />
                  </Box>
                  <Box className={classes.chipRow}>
                    <Chip size="small" label={accessStatus.label} className={accessStatus.className} />
                    {isMovingFromAnotherCrew ? <Chip size="small" label={`Moves from ${member.currentCrewName}`} className="df-chip-warn" /> : null}
                  </Box>
                </div>
              )
            }) : (
              <PortalStateCard
                title="No users available"
                message="No team members available. Invite people from the Users tab first."
                minHeight={180}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : (editingCrew?._id ? 'Save Crew' : 'Create Crew')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user,
  notification: state.notification
})

export default connect(mapStateToProps)(SubcontractorCrews)
